import { memo, useState, useCallback } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { NodeData } from '@/types/workflow';
import { Filter, Copy, Trash2, Plus, X } from 'lucide-react';

interface CategorizerNodeProps extends NodeProps {
  data: NodeData;
}

export const CategorizerNode = memo(({ data, selected, id }: CategorizerNodeProps) => {
  const [showToolbar, setShowToolbar] = useState(false);
  const { getNodes, setNodes, getEdges, setEdges } = useReactFlow();
  const nodeColor = '#ec4899'; // Pink for AI processing

  const updateNodeData = useCallback((updates: Partial<NodeData>) => {
    const nodes = getNodes();
    const updatedNodes = nodes.map(node => {
      if (node.id === id) {
        return {
          ...node,
          data: {
            ...node.data,
            ...updates
          }
        };
      }
      return node;
    });
    setNodes(updatedNodes);
  }, [id, getNodes, setNodes]);

  const handleDuplicate = useCallback(() => {
    const nodes = getNodes();
    const currentNode = nodes.find(node => node.id === id);
    if (currentNode) {
      const newNode = {
        ...currentNode,
        id: `${currentNode.type}_${Date.now()}`,
        position: {
          x: currentNode.position.x + 50,
          y: currentNode.position.y + 50,
        },
      };
      setNodes([...nodes, newNode]);
    }
  }, [id, getNodes, setNodes]);

  const handleDelete = useCallback(() => {
    const nodes = getNodes();
    const edges = getEdges();
    
    const updatedNodes = nodes.filter(node => node.id !== id);
    const updatedEdges = edges.filter(edge => edge.source !== id && edge.target !== id);
    
    setNodes(updatedNodes);
    setEdges(updatedEdges);
  }, [id, getNodes, getEdges, setNodes, setEdges]);

  const categories = data.categories || [
    { name: 'Enterprise', description: 'Large companies with 500+ employees' },
    { name: 'SMB', description: 'Small to medium business 50-500 employees' },
    { name: 'Startup', description: 'Early stage companies under 50 employees' }
  ];

  const addCategory = () => {
    const newCategories = [...categories, { name: '', description: '' }];
    updateNodeData({ categories: newCategories });
  };

  const removeCategory = (index: number) => {
    const newCategories = categories.filter((_: any, i: number) => i !== index);
    updateNodeData({ categories: newCategories });
  };

  const updateCategory = (index: number, field: string, value: string) => {
    const newCategories = [...categories];
    newCategories[index] = { ...newCategories[index], [field]: value };
    updateNodeData({ categories: newCategories });
  };

  return (
    <div 
      className="relative group"
      onMouseEnter={() => setShowToolbar(true)}
      onMouseLeave={() => setShowToolbar(false)}
    >
      {/* Toolbar */}
      {(showToolbar || selected) && (
        <div className="absolute -top-10 left-1/2 -translate-x-1/2 z-10">
          <div className="flex items-center gap-1 bg-background/95 backdrop-blur-sm border rounded-lg shadow-lg px-2 py-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 hover:bg-muted"
              onClick={handleDuplicate}
            >
              <Copy className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 text-destructive hover:bg-destructive hover:text-destructive-foreground"
              onClick={handleDelete}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}

      <Card 
        className={`min-w-[350px] backdrop-blur-md bg-background/75 border-pink-500/30 transition-all duration-200 ${
          selected 
            ? 'ring-2 ring-pink-500/50 shadow-lg shadow-pink-500/20' 
            : 'shadow-md hover:shadow-lg'
        }`}
      >
        <CardHeader className="pb-2">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-pink-500/20 flex items-center justify-center">
              <Filter className="w-4 h-4 text-pink-400" />
            </div>
            <div className="flex-1">
              <div className="font-medium text-sm">{data.label || 'Categorizer'}</div>
              <div className="text-xs text-muted-foreground">AI categorization into custom buckets</div>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="pt-0 space-y-3">
          <div>
            <Label className="text-xs">Categorization Instructions</Label>
            <Textarea
              placeholder="Categorize leads based on company size, industry, and potential value. Use the provided categories..."
              value={data.categorizationInstructions || ''}
              onChange={(e) => updateNodeData({ categorizationInstructions: e.target.value })}
              className="min-h-[60px] resize-none text-xs"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs">Categories</Label>
              <Button
                size="sm"
                variant="outline"
                onClick={addCategory}
                className="h-6 w-6 p-0"
              >
                <Plus className="w-3 h-3" />
              </Button>
            </div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {categories.map((category: any, index: number) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    placeholder="Category name"
                    value={category.name}
                    onChange={(e) => updateCategory(index, 'name', e.target.value)}
                    className="h-8 flex-1"
                  />
                  <Input
                    placeholder="Description/criteria"
                    value={category.description}
                    onChange={(e) => updateCategory(index, 'description', e.target.value)}
                    className="h-8 flex-[2]"
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeCategory(index)}
                    className="h-6 w-6 p-0 hover:bg-destructive hover:text-destructive-foreground"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="p-2 bg-pink-50 dark:bg-pink-950/20 rounded-md border border-pink-200 dark:border-pink-800">
            <div className="text-xs text-pink-700 dark:text-pink-300">
              🏷️ AI will analyze input data and assign it to the most appropriate category
            </div>
          </div>
        </CardContent>
        
        {/* Input Handle */}
        <Handle
          type="target"
          position={Position.Left}
          id="input"
          className="custom-handle"
        />
        
        {/* Output Handle */}
        <Handle
          type="source"
          position={Position.Right}
          id="categorized"
          className="custom-handle"
          style={{
            backgroundColor: '#ec4899',
            boxShadow: '0 2px 8px rgba(236, 72, 153, 0.3)',
          }}
        />
      </Card>
    </div>
  );
});

CategorizerNode.displayName = 'CategorizerNode';